import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { formatHoraSP } from '@fiodecobre/shared';

interface UsuarioOpcao {
  id: number;
  nome: string;
  ativo: boolean;
}

interface EventoDiario {
  quando: string;
  usuario_id: number | null;
  usuario_nome: string;
  modulo: string;
  acao: string;
  referencia_tipo: string | null;
  referencia_id: number | null;
  referencia_rotulo: string | null;
  detalhe: string;
  hora_desconhecida: boolean;
}

function hojeSP(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

// Aritmetica de calendario pura (UTC nos componentes da data, nunca interpretando como
// horario local) — soma/subtrai dia sem sofrer deslocamento de fuso.
function addDias(dataIso: string, delta: number): string {
  const [y, m, d] = dataIso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

function fmtDataBR(dataIso: string): string {
  const [y, m, d] = dataIso.split('-');
  return `${d}/${m}/${y}`;
}

// Ordem fixa dos modulos na fila de filtros — NAO ordenar por contagem, que muda todo dia e
// faria os botoes trocarem de lugar debaixo do dedo. Vem primeiro o que se procura mais.
const MODULOS = [
  'Vendas', 'Caixa', 'Financeiro', 'Orcamentos', 'Fiscal',
  'Notas de entrada', 'Trocas', 'Ponto', 'Acesso', 'Atendimento', 'Estoque', 'Produtos',
] as const;

// Produtos (480-630/dia) e Estoque (100-240/dia) sao ~80% das linhas: abrir com os dois ligados
// afoga venda/caixa/dinheiro numa parede de 900 registros. Ficam DESLIGADOS no primeiro acesso —
// mas o botao continua na tela com o contador dentro, entao o volume nao some, so sai da lista.
const DESLIGADOS_PADRAO = ['Produtos', 'Estoque'];
const CHAVE_MODULOS = 'fdc_diario_modulos';

// Alem dos ligados, o localStorage guarda quais modulos EXISTIAM quando a escolha foi feita.
// Sem isso, um modulo novo (Acesso, na Fase 2) nasceria fora da lista salva e portanto
// DESLIGADO pra quem ja tinha usado a tela — apareceria como se nao houvesse dado nenhum.
// Modulo que o usuario nunca viu entra pelo padrao, nao pela ausencia.
interface EscolhaModulos { ligados: string[]; conhecidos: string[] }

function padraoLigados(): string[] {
  return MODULOS.filter((m) => !DESLIGADOS_PADRAO.includes(m));
}

function lerModulosSalvos(): string[] {
  try {
    const cru = localStorage.getItem(CHAVE_MODULOS);
    if (cru) {
      const salvo = JSON.parse(cru);
      // Formato antigo (array puro, Fase 1): os conhecidos daquela epoca sao os que ele deixou
      // ligados + os dois que ja nasciam desligados. O resto e novo e entra pelo padrao.
      const { ligados, conhecidos }: EscolhaModulos = Array.isArray(salvo)
        ? { ligados: salvo, conhecidos: [...salvo, ...DESLIGADOS_PADRAO] }
        : salvo;
      if (Array.isArray(ligados) && Array.isArray(conhecidos)) {
        const novos = MODULOS.filter((m) => !conhecidos.includes(m) && !DESLIGADOS_PADRAO.includes(m));
        return MODULOS.filter((m) => ligados.includes(m) || novos.includes(m));
      }
    }
  } catch { /* localStorage indisponivel ou json corrompido — cai no padrao */ }
  return padraoLigados();
}

function linkReferencia(tipo: string | null, id: number | null): string | null {
  if (!tipo || id === null) return null;
  if (tipo === 'orcamento') return `/orcamentos/${id}`;
  if (tipo === 'produto') return `/produtos/${id}`;
  return null;
}

// Tela "Diario" (/equipe/diario) — linha do tempo do dia, montada em cima das colunas de
// autor/horario que ja existem nas tabelas de negocio. Sem tabela de auditoria nova: o que
// nao esta coberto, o rodape abaixo declara.
// Cada usuario ve o PROPRIO dia; so admin escolhe a pessoa (ou "Todos"). O seletor sumir pra
// quem nao e admin e conforto, nao seguranca — quem barra e o servidor, que ignora o
// usuario_id da query e forca o id do token.
export function DiarioUsuario() {
  const toast = useToast();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const [usuarios, setUsuarios] = useState<UsuarioOpcao[]>([]);
  // Nao-admin ja nasce fixado em si mesmo: alem de casar com o que o servidor vai devolver,
  // e o que tira a coluna "Usuario" da tabela (redundante quando so ha uma pessoa).
  const [usuarioId, setUsuarioId] = useState(() => (usuario?.perfil === 'admin' ? '' : String(usuario?.id ?? '')));
  const [data, setData] = useState(() => hojeSP());
  const [eventos, setEventos] = useState<EventoDiario[]>([]);
  const [truncado, setTruncado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ligados, setLigados] = useState<string[]>(() => lerModulosSalvos());

  useEffect(() => {
    const escolha: EscolhaModulos = { ligados, conhecidos: [...MODULOS] };
    try { localStorage.setItem(CHAVE_MODULOS, JSON.stringify(escolha)); } catch { /* ignora */ }
  }, [ligados]);

  useEffect(() => {
    // So admin escolhe pessoa — pra quem nao e, a lista nao serve a nada. Vale pro GERENTE em
    // especial: o /equipe devolve a loja inteira pra ele, e povoar um seletor que o servidor vai
    // ignorar so ofereceria uma escolha falsa.
    if (!ehAdmin) return;
    (async () => {
      try {
        // Reusa o mesmo endpoint da tabela de Ponto — ja inclui inativos (?inativos=1, DA-397)
        // e ja e a fonte que a tela conhece pra listar quem trabalhou/trabalha na loja.
        const r = await api.get<UsuarioOpcao[]>('/api/funcionarios/equipe', { query: { inativos: 1 } });
        setUsuarios([...r].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      } catch (e: any) {
        toast.erro(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehAdmin]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get<{ eventos: EventoDiario[]; truncado: boolean }>('/api/equipe/diario', {
          query: { usuario_id: usuarioId || undefined, data },
        });
        setEventos(r.eventos);
        setTruncado(r.truncado);
      } catch (e: any) {
        toast.erro(e.message);
        setEventos([]);
        setTruncado(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [usuarioId, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const pessoaFiltrada = useMemo(
    () => usuarios.find((u) => String(u.id) === usuarioId)?.nome,
    [usuarios, usuarioId],
  );

  // Contagem sai do dia INTEIRO (nao do recorte visivel) — e o que mantem o volume a mostra
  // mesmo com o modulo desligado.
  const contagem = useMemo(() => {
    const c: Record<string, number> = {};
    for (const ev of eventos) c[ev.modulo] = (c[ev.modulo] || 0) + 1;
    return c;
  }, [eventos]);

  const visiveis = useMemo(() => eventos.filter((ev) => ligados.includes(ev.modulo)), [eventos, ligados]);
  const escondidos = eventos.length - visiveis.length;
  const todosLigados = MODULOS.every((m) => ligados.includes(m));

  function alternar(modulo: string) {
    setLigados((atual) => (atual.includes(modulo) ? atual.filter((m) => m !== modulo) : [...atual, modulo]));
  }

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <ScrollText size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Diario
        </h2>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, margin: '0 0 12px 2px' }}>
        {ehAdmin ? (
          <select value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} style={{ minWidth: 220 }}>
            <option value="">Todos</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}{!u.ativo ? ' (inativo)' : ''}</option>
            ))}
          </select>
        ) : (
          <div style={{ minWidth: 220, fontWeight: 600 }}>{usuario?.nome}</div>
        )}

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button type="button" className="btn-icon" onClick={() => setData((d) => addDias(d, -1))} title="Dia anterior">
            <ChevronLeft size={18} />
          </button>
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={{ width: 150 }} />
          <button
            type="button"
            className="btn-icon"
            onClick={() => setData((d) => addDias(d, 1))}
            title="Proximo dia"
            disabled={data >= hojeSP()}
          >
            <ChevronRight size={18} />
          </button>
          <button type="button" className="btn-secundario" onClick={() => setData(hojeSP())} disabled={data === hojeSP()}>
            Hoje
          </button>
        </div>
      </div>

      {/* Fila de modulos: o contador fica DENTRO do botao, ligado ou desligado — desligar tira da
          lista, nunca esconde que aquilo aconteceu. Modulo sem nada no dia fica inerte. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, margin: '0 0 12px 2px' }}>
        {MODULOS.map((m) => {
          const n = contagem[m] || 0;
          const on = ligados.includes(m);
          return (
            <button
              key={m}
              type="button"
              onClick={() => alternar(m)}
              disabled={n === 0}
              title={n === 0 ? `Nada de ${m} nesse dia` : on ? `Tirar ${m} da lista` : `Trazer ${m} pra lista`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: n === 0 ? 'default' : 'pointer',
                opacity: n === 0 ? 0.4 : 1,
                background: on ? 'var(--primary)' : 'transparent',
                color: on ? '#fff' : 'var(--text-muted)',
                border: `1px solid ${on ? 'var(--primary)' : 'var(--input-border)'}`,
              }}
            >
              {m}
              <span style={{
                fontVariantNumeric: 'tabular-nums',
                background: on ? 'rgba(255,255,255,0.25)' : 'var(--input-border)',
                color: on ? '#fff' : 'var(--text-muted)',
                borderRadius: 999, padding: '0 6px', fontSize: 11,
              }}>{n}</span>
            </button>
          );
        })}
        {!todosLigados && (
          <button type="button" className="btn-secundario" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setLigados([...MODULOS])}>
            Ligar todos
          </button>
        )}
      </div>

      {truncado && (
        <div style={{ background: '#f97316', color: '#000', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 10, fontWeight: 600 }}>
          Muito movimento nesse dia — a lista foi cortada nos primeiros {eventos.length} registros. Filtre por pessoa pra ver o resto.
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : eventos.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nenhum registro para {pessoaFiltrada || 'todos'} em {fmtDataBR(data)}.
        </div>
      ) : visiveis.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          Os {escondidos} registros de {fmtDataBR(data)} estao todos em modulos desligados — ligue um acima pra ver.
        </div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 70 }}>Hora</th>
              {!usuarioId && <th style={{ width: 160 }}>Usuario</th>}
              <th style={{ width: 130 }}>Modulo</th>
              <th>O que fez</th>
              <th>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((ev, i) => {
              const link = linkReferencia(ev.referencia_tipo, ev.referencia_id);
              return (
                <tr key={i}>
                  {/* Carimbo sem hora (o banco so guarda a data) mostra "—", nunca "00:00" —
                      exibir meia-noite seria inventar um horario que ninguem registrou. */}
                  <td style={{ fontVariantNumeric: 'tabular-nums' }} title={ev.hora_desconhecida ? 'O sistema so guarda a data desse lancamento, sem hora' : undefined}>
                    {ev.hora_desconhecida ? '—' : formatHoraSP(ev.quando)}
                  </td>
                  {!usuarioId && <td>{ev.usuario_nome}</td>}
                  <td>{ev.modulo}</td>
                  <td>
                    {link ? (
                      <button
                        type="button"
                        onClick={() => navigate(link)}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                      >
                        {ev.acao}
                      </button>
                    ) : ev.acao}
                    {ev.referencia_rotulo && (
                      <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 12 }}>{ev.referencia_rotulo}</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{ev.detalhe}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--card-bg, #f9fafb)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        O que o Diario NAO enxerga: a saida do sistema so aparece quando a pessoa clica em "Sair" — fechar o navegador ou
        desligar o computador nao deixa registro (e como o acesso vale 30 dias, ela costuma entrar uma vez e passar semanas
        sem logar de novo; quem conta o horario de chegada de cada dia e o Ponto); a senha gerencial registra quem PEDIU a
        liberacao, nao qual gerente autorizou; exclusoes nao deixam rastro; cadastro de cliente e de fornecedor nao guarda
        quem criou; desconto dado no fechamento do caixa ainda nao carimba o operador.
      </div>
    </div>
  );
}
